import type { VizTone } from '@/versions/v1/modules/_viz/viz-tone';
import type { KpiTile, RoleTable } from '@/versions/v1/schema/plant-role';
import type { SampleAdherence } from '@/versions/v1/schema/unit-operation';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { HseMetricId, HseSafetyMetric } from '@/versions/v1/schema/hse';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { parseSampleAdherence } from '@/versions/v1/shared/StateChip';
import { BUNKER_HILL_ASSIGNMENTS, BUNKER_HILL_CREWS, BUNKER_HILL_PERSONS, BUNKER_HILL_SEATS } from '@/versions/v1/shared/bunker-hill-org-seed';
import {
  CREW_COMPLIANCE_TREND_7D,
  EXTRA_ROLE_TABLES,
  ROLE_KPIS,
  ROLE_TABLES,
  SHIFT_COMPLIANCE_CHECKS,
  type BunkerHillTrendBundle,
  type ShiftComplianceCheck,
} from '@/versions/v1/shared/bunker-hill-seed';
import {
  HSE_CURSOR,
  HSE_INSPECTIONS,
  HSE_METRIC_TARGETS,
  HSE_OBLIGATIONS,
  HSE_PERMITS,
  HSE_RATE_BASIS_HOURS,
  HSE_REQUIREMENTS,
  HSE_SAFETY_MONTHS,
  LAST_LTI_DATE,
} from '@/versions/v1/shared/bunker-hill-hse-seed';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { valueAt } from '@/versions/v1/shared/series.derive';
import { addMinutes, diffMinutes, shiftOf } from '@/versions/v1/shared/clock.derive';

export type ComplianceTab = 'permits' | 'compliance' | 'crew' | 'trend';
export type LegacyComplianceTab = 'crew' | 'shift';

export type ComplianceCopy = {
  title: string;
  description: string;
};

export type SamplesOnTimeSummary = {
  onTime: number;
  total: number;
  limsPending: number;
  label: string;
  sub: string;
};

export type RecordsCompleteSummary = {
  complete: number;
  total: number;
  label: string;
  sub: string;
};

const ALL_TABLES: readonly RoleTable[] = [...ROLE_TABLES, ...EXTRA_ROLE_TABLES];

const SAMPLE_COMPLIANCE_FOOTER_ID = 'sample-compliance-footer';
const SAMPLE_PAST_COLUMNS = ['06:00', '08:00', '10:00'] as const;
const SAMPLE_FUTURE_COLUMNS = ['14:00', '16:00'] as const;

/** Paul's HSE dashboard is the page (locked comp `compliance.html` B,
 *  adjudication 1): the header, band and 8/4 row stay put, and these four
 *  tabs are the drill-downs, the permit register first. */
export const COMPLIANCE_TABS: { id: ComplianceTab; label: string }[] = [
  { id: 'permits', label: 'Permits to work' },
  { id: 'compliance', label: 'Regulatory compliance' },
  { id: 'crew', label: 'Crew compliance' },
  { id: 'trend', label: 'Safety trend' },
];

export const HSE_TAB_IDS = COMPLIANCE_TABS.map((tab) => tab.id) as readonly ComplianceTab[];

/** `?tab=dashboard` (the retired tab) resolves to the page with its default tab. */
const COMPLIANCE_TAB_ALIASES: Readonly<Record<string, ComplianceTab>> = { dashboard: 'permits' };

export function complianceTabFromSearchParam(param: string | null): ComplianceTab | undefined {
  if (!param) return undefined;
  return COMPLIANCE_TABS.find((tab) => tab.id === param)?.id ?? COMPLIANCE_TAB_ALIASES[param];
}

export function complianceCopy(_plantName: string): ComplianceCopy {
  return {
    title: 'HSE & regulatory compliance',
    description: '',
  };
}

export function kpisForComplianceTab(tab: LegacyComplianceTab): KpiTile[] {
  const roleId = tab === 'crew' ? 'superintendent' : 'ops-supervisor';
  const tabId = tab === 'crew' ? 'crew' : 'shift-compliance';
  return ROLE_KPIS.filter((kpi) => kpi.roleId === roleId && kpi.tabId === tabId).map((kpi) => {
    if (tab !== 'crew' || kpi.id !== 'crew-sample') return kpi;
    const count = ratioFromCount(kpi.value);
    if (!count) return kpi;
    const percentage = (count.numerator / count.denominator) * 100;
    return {
      ...kpi,
      value: `${percentage.toFixed(1)}%`,
      sub: `${count.numerator}/${count.denominator} on time · ${percentage < 90 ? 'below 90%' : 'at or above 90%'}`,
      tone: percentage < 90 ? 'warn' : 'ok',
    };
  });
}

export function tablesForComplianceTab(tab: LegacyComplianceTab): RoleTable[] {
  const roleId = tab === 'crew' ? 'superintendent' : 'ops-supervisor';
  const tabId = tab === 'crew' ? 'crew' : 'shift-compliance';
  return ALL_TABLES.filter((table) => table.roleId === roleId && table.tabId === tabId);
}

export function shiftComplianceChecks(): readonly ShiftComplianceCheck[] {
  return SHIFT_COMPLIANCE_CHECKS;
}

export type SampleLegendEntry = { state: SampleAdherence; meaning: string };

/** One-row legend for the seven-state sample chips (recognition over recall). */
export function sampleAdherenceLegend(): readonly SampleLegendEntry[] {
  return [
    { state: 'REG', meaning: 'registered on time' },
    { state: 'PENDING', meaning: 'result pending in LIMS' },
    { state: 'LATE', meaning: 'taken late' },
    { state: 'MISS', meaning: 'missed' },
    { state: 'DUE', meaning: 'due this hour' },
    { state: 'UPCOMING', meaning: 'scheduled later' },
    { state: 'N/A', meaning: 'not scheduled' },
  ];
}

/** The crew matrix's total row, one honest basis per column (S10): Shifts sums
 *  its four shifts (14), Samples pools its counts (73/85), and Rounds, Reports,
 *  Reagents and Overall carry the unweighted crew mean — 90.7 %, 100 %, 93 %
 *  and 92.3 % — at the precision their own cells are read in. LIMS TAT is the
 *  mean of the four displayed turnarounds (1.8 + 2.4 + 1.5 + 2.9) ÷ 4 = 2.15 h
 *  read as 2.2 h. Nothing here is a typed figure. */
const CREW_TOTAL_ROW_ID = 'cr-t';
const CREW_MEAN_COLUMNS = new Set(['Rounds', 'Reports', 'Reagents', 'LIMS TAT', 'Overall']);

/** A displayed figure → tenths, so a crew mean rounds half-up the way the cell
 *  is read, without binary-float drift (2.15 → 2.2, not 2.1). */
function tenthsOf(value: string): number | null {
  const match = value.trim().match(/^(\d+(?:\.\d+)?)/);
  return match ? Math.round(Number.parseFloat(match[1]!) * 10) : null;
}

function crewMeanCell(index: number, column: string, crewRows: readonly { cells: readonly string[] }[]): string | null {
  const tenths = crewRows.map((row) => tenthsOf(row.cells[index] ?? ''));
  if (tenths.some((value) => value === null)) return null;
  const sum = tenths.reduce<number>((total, value) => total + (value ?? 0), 0);
  const mean = Math.round(sum / crewRows.length);
  const decimals = crewRows.some((row) => /\.\d/.test(row.cells[index] ?? '')) ? 1 : 0;
  return column === 'LIMS TAT' ? `${(mean / 10).toFixed(decimals)}h` : `${(mean / 10).toFixed(decimals)}%`;
}

function crewTotalRowCells(
  columns: readonly string[],
  crewRows: readonly { cells: readonly string[] }[],
  seeded: readonly string[],
): readonly string[] {
  return columns.map((column, index) => {
    if (index === 0) return 'Total · crew avg';
    if (column === 'Shifts') {
      return String(crewRows.reduce((sum, row) => sum + (Number.parseInt(row.cells[index] ?? '', 10) || 0), 0));
    }
    if (column === 'Samples') {
      const counts = crewRows.map((row) => ratioFromCount(row.cells[index] ?? ''));
      if (counts.every((count) => count !== null)) {
        const numerator = counts.reduce((sum, count) => sum + (count?.numerator ?? 0), 0);
        const denominator = counts.reduce((sum, count) => sum + (count?.denominator ?? 0), 0);
        if (denominator > 0) {
          return `${((numerator / denominator) * 100).toFixed(1)}% (${numerator}/${denominator})`;
        }
      }
    }
    if (CREW_MEAN_COLUMNS.has(column)) {
      const mean = crewMeanCell(index, column, crewRows);
      if (mean !== null) return mean;
    }
    return seeded[index] ?? '';
  });
}

/** Derive the crew table's total row from its own four crew rows per S10 —
 *  distinct from the plant days the trend card draws — and name it for both
 *  the sums and the means, so no typed second figure survives in the seed. */
export function crewTableWithRollingLabel(table: RoleTable): RoleTable {
  const crewRows = table.rows.filter((row) =>
    BUNKER_HILL_CREWS.some((crew) => crew.id === row.id),
  );
  if (crewRows.length === 0) {
    return table;
  }
  const sampleColumn = table.columns.indexOf('Samples');
  const seededTotal = table.rows.find((row) => row.id === CREW_TOTAL_ROW_ID);
  const totalCells = seededTotal
    ? crewTotalRowCells(table.columns, crewRows, seededTotal.cells)
    : null;
  return {
    ...table,
    rows: table.rows.map((row) => {
      if (row.id === CREW_TOTAL_ROW_ID && totalCells) {
        return { ...row, cells: totalCells };
      }
      const cells = row.cells.map((cell, index) => {
        if (index === sampleColumn) {
          const count = ratioFromCount(cell);
          if (count) {
            const percentage = (count.numerator / count.denominator) * 100;
            return `${percentage.toFixed(1)}% (${count.numerator}/${count.denominator})`;
          }
        }
        return cell;
      });
      return { ...row, cells };
    }),
  };
}

function ratioFromCount(value: string): { numerator: number; denominator: number } | null {
  const match = value.match(/^(\d+)\/(\d+)$/);
  if (!match) return null;
  const numerator = Number(match[1]);
  const denominator = Number(match[2]);
  return denominator > 0 ? { numerator, denominator } : null;
}

export function isSampleTrackingTable(table: RoleTable): boolean {
  return table.caption.startsWith('Sample tracking');
}

export function isSampleComplianceFooterRow(rowId: string): boolean {
  return rowId === SAMPLE_COMPLIANCE_FOOTER_ID;
}

/** S11 — Zn rougher conc 10:00 MISS links to the confidence-discount case. */
export function shiftGridCaseId(
  rowId: string,
  columnHeader: string,
  cell: string,
): string | undefined {
  if (rowId === 'ss-7' && columnHeader === '10:00' && parseSampleAdherence(cell) === 'MISS') {
    return 'case-zn-recovery';
  }
  return undefined;
}

function sampleDataRows(table: RoleTable) {
  return table.rows.filter((row) => !isSampleComplianceFooterRow(row.id));
}

function sampleCellState(table: RoleTable, rowId: string, columnHeader: string): SampleAdherence | null {
  const row = table.rows.find((entry) => entry.id === rowId);
  if (!row) return null;
  const index = table.columns.indexOf(columnHeader);
  if (index <= 0) return null;
  return parseSampleAdherence(row.cells[index] ?? '');
}

/** Layer A footer arithmetic for one sample-grid time column. */
export function sampleColumnComplianceFooter(table: RoleTable, columnHeader: string): string {
  if (columnHeader === '12:00') {
    const due = sampleDataRows(table).some(
      (row) => sampleCellState(table, row.id, columnHeader) === 'DUE',
    );
    return due ? 'DUE' : '—';
  }
  if ((SAMPLE_FUTURE_COLUMNS as readonly string[]).includes(columnHeader)) {
    return '—';
  }

  let scheduled = 0;
  let complete = 0;
  for (const row of sampleDataRows(table)) {
    const state = sampleCellState(table, row.id, columnHeader);
    if (state === null || state === 'N/A') continue;
    scheduled += 1;
    if (state === 'REG') complete += 1;
  }
  if (scheduled === 0) return '—';
  const pct = Math.round((complete / scheduled) * 100);
  return `${complete}/${scheduled} ${pct}%`;
}

export function sampleTrackingTableWithFooter(table: RoleTable): RoleTable {
  if (!isSampleTrackingTable(table)) return table;
  const footerCells = table.columns.slice(1).map((column) => sampleColumnComplianceFooter(table, column));
  return {
    ...table,
    rows: [
      ...sampleDataRows(table),
      {
        id: SAMPLE_COMPLIANCE_FOOTER_ID,
        cells: ['Compliance →', ...footerCells],
      },
    ],
  };
}

/** Reconstruct Samples on time from past sample-grid slots (Layer A). */
export function samplesOnTimeFromGrid(table: RoleTable): SamplesOnTimeSummary {
  let onTime = 0;
  let total = 0;
  let limsPending = 0;

  for (const row of sampleDataRows(table)) {
    for (const columnHeader of SAMPLE_PAST_COLUMNS) {
      const state = sampleCellState(table, row.id, columnHeader);
      if (state === null || state === 'N/A') continue;
      total += 1;
      if (state === 'REG') onTime += 1;
      if (state === 'PENDING') limsPending += 1;
    }
  }

  return {
    onTime,
    total,
    limsPending,
    label: `${onTime}/${total}`,
    sub: limsPending === 1 ? '1 LIMS pending' : `${limsPending} LIMS pending`,
  };
}

/** Records complete from shift checks — DUE is the only incomplete state. */
export function recordsCompleteFromChecks(
  checks: readonly ShiftComplianceCheck[] = SHIFT_COMPLIANCE_CHECKS,
): RecordsCompleteSummary {
  const total = checks.length;
  const complete = checks.filter((check) => check.state !== 'DUE').length;
  const calPending = checks.filter((check) => check.state === 'DUE').length;
  return {
    complete,
    total,
    label: `${complete}/${total}`,
    sub: calPending === 1 ? '1 cal pending' : `${calPending} cal pending`,
  };
}

export function crewComplianceTrend7d(): BunkerHillTrendBundle {
  return CREW_COMPLIANCE_TREND_7D;
}

const CREW_PCT_COLUMNS = new Set(['Samples', 'Rounds', 'Reports', 'Reagents', 'Overall']);

/** Slide 4 cell tone by value, quiet in band (locked comp `compliance.html`
 *  binding list): < 85 red · 85–90 amber · ≥ 90 ink, no tone. */
export function crewComplianceCellTone(columnHeader: string, cell: string): VizTone | null {
  if (!CREW_PCT_COLUMNS.has(columnHeader)) return null;
  const match = cell.match(/([\d.]+)%/);
  if (!match) return null;
  const pct = Number.parseFloat(match[1]);
  if (!Number.isFinite(pct)) return null;
  if (pct < 85) return 'bad';
  if (pct < 90) return 'warn';
  return null;
}

export function shiftComplianceMeter(): { value: number; target: number } {
  return { value: 88.6, target: 95 };
}

/** Map deck action priority to the plant P-tier vocabulary (A §M1 §7). */
export function actionPriorityLabel(priority: string): string {
  // A §M1 §7 — plant's own 4-tier scheme
  if (priority === 'URGENT') return 'P1 · URGENT';
  if (priority === 'HIGH') return 'P2 · HIGH';
  if (priority === 'MED') return 'P3 · MED';
  return priority;
}

// --- HSE dashboard, permits and regulatory compliance ---------------------

export type HseKpi = {
  id: string;
  label: string;
  value: string;
  sub: string;
  tone: VizTone;
};

export type HseRequirementRow = {
  id: string;
  label: string;
  dueAt: SeriesTime;
  dueLabel: string;
  detail: string;
  tone: VizTone;
};

export type HseDashboardView = {
  kpis: readonly HseKpi[];
  safety: {
    trifr: number;
    ltifr: number;
    /** TRIFR over the prior operating year (Mar–Feb), the band's comparison. */
    priorOperatingYearTrifr: number;
    rateBasisHours: number;
    /** `Year 1 from 1 Mar 2026 · prior year Mar 2025 – Feb 2026`. */
    periodMeta: string;
    yearToDateLabel: string;
    priorOperatingYearLabel: string;
    metrics: readonly HseSafetyMetric[];
  };
  upcoming: readonly HseRequirementRow[];
};

export type HsePermitRow = {
  id: string;
  type: string;
  equipmentId: string;
  location: string;
  crewPersonIds: readonly string[];
  crew: string;
  issued: string;
  expires: string;
  area: string;
  status: 'Active' | 'Closed' | 'Expired' | 'Scheduled';
};

export type HsePermitRegisterView = {
  activeCount: number;
  activeCaption: string;
  closedTodayCount: number;
  closedTodayCaption: string;
  expiringWithinTwoHoursCount: number;
  expiringCaption: string;
  rows: readonly HsePermitRow[];
};

export type RegulatoryObligationRow = {
  id: string;
  requirement: string;
  frequency: string;
  lastCompleted: string;
  nextDue: string;
  status: 'Current' | 'Due soon' | 'Overdue';
  authority: string;
  ownerSeatId: string;
  readingId?: string;
  readingValue?: number;
  readingLabel?: string;
};

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const MAX_DUE_SOON_MINUTES = 14 * 24 * 60;
const TWO_HOURS_MINUTES = 2 * 60;
const DAY_MILLISECONDS = 24 * 60 * 60 * 1000;
const ROLLING_DAYS = 365;

function monthIndex(month: string): number {
  const [year, value] = month.split('-').map(Number);
  return (year ?? 0) * 12 + ((value ?? 1) - 1);
}

function cursorMonthIndex(cursor: SeriesTime): number {
  return monthIndex(cursor.slice(0, 7));
}

function dateParts(value: string): { year: number; month: number; day: number } {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) throw new Error(`Invalid HSE date ${value}`);
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

function formatDate(value: string): string {
  const { year, month, day } = dateParts(value);
  return `${day} ${MONTH_LABELS[month - 1]} ${year}`;
}

function formatShortDate(value: string): string {
  const { month, day } = dateParts(value);
  return `${day} ${MONTH_LABELS[month - 1]}`;
}

function formatDateTime(value: SeriesTime): string {
  return `${formatShortDate(value)} · ${value.slice(11, 16)}`;
}

function dateAtUtcMidnight(value: string): number {
  const { year, month, day } = dateParts(value);
  return Date.UTC(year, month - 1, day);
}

function daysBetween(start: string, end: string): number {
  return Math.floor((dateAtUtcMidnight(end) - dateAtUtcMidnight(start)) / (24 * 60 * 60 * 1000));
}

function sumMonths(months: readonly (typeof HSE_SAFETY_MONTHS)[number][]) {
  return months.reduce(
    (sum, row) => ({
      recordableInjuries: sum.recordableInjuries + row.recordableInjuries,
      lostTimeInjuries: sum.lostTimeInjuries + row.lostTimeInjuries,
      restrictedWorkCases: sum.restrictedWorkCases + row.restrictedWorkCases,
      nearMisses: sum.nearMisses + row.nearMisses,
      hazardReports: sum.hazardReports + row.hazardReports,
      hoursWorked: sum.hoursWorked + row.hoursWorked,
      toolboxTalksCompleted: sum.toolboxTalksCompleted + row.toolboxTalksCompleted,
      toolboxTalksScheduled: sum.toolboxTalksScheduled + row.toolboxTalksScheduled,
    }),
    {
      recordableInjuries: 0,
      lostTimeInjuries: 0,
      restrictedWorkCases: 0,
      nearMisses: 0,
      hazardReports: 0,
      hoursWorked: 0,
      toolboxTalksCompleted: 0,
      toolboxTalksScheduled: 0,
    },
  );
}

function safetyMonthsAvailableAt(cursor: SeriesTime) {
  return HSE_SAFETY_MONTHS.filter((row) => !row.asOf || diffMinutes(row.asOf, cursor) <= 0);
}

function countValue(value: number): string {
  return String(value);
}

function safetyMetricRows(
  current: ReturnType<typeof sumMonths>,
  prior: ReturnType<typeof sumMonths>,
): HseSafetyMetric[] {
  const values: Record<HseMetricId, { label: string; current: string; prior: string }> = {
    lti: {
      label: 'Lost time injuries (LTI)',
      current: countValue(current.lostTimeInjuries),
      prior: countValue(prior.lostTimeInjuries),
    },
    mti: {
      label: 'Medical treatment injuries (MTI)',
      current: countValue(current.recordableInjuries - current.lostTimeInjuries - current.restrictedWorkCases),
      prior: countValue(prior.recordableInjuries - prior.lostTimeInjuries - prior.restrictedWorkCases),
    },
    'restricted-work': {
      label: 'Restricted work cases',
      current: countValue(current.restrictedWorkCases),
      prior: countValue(prior.restrictedWorkCases),
    },
    'near-misses': {
      label: 'Near misses reported',
      current: countValue(current.nearMisses),
      prior: countValue(prior.nearMisses),
    },
    'hazard-reports': {
      label: 'Hazard reports raised',
      current: countValue(current.hazardReports),
      prior: countValue(prior.hazardReports),
    },
    'toolbox-talks': {
      label: 'Toolbox talks completed',
      current: `${Math.round((current.toolboxTalksCompleted / current.toolboxTalksScheduled) * 100)}%`,
      prior: `${Math.round((prior.toolboxTalksCompleted / prior.toolboxTalksScheduled) * 100)}%`,
    },
  };

  return (Object.keys(values) as HseMetricId[]).map((id) => ({
    id,
    label: values[id].label,
    yearToDate: values[id].current,
    priorOperatingYear: values[id].prior,
    target: HSE_METRIC_TARGETS[id],
  }));
}

function roundOne(value: number): number {
  return Math.round(value * 10) / 10;
}

function monthsForOperatingYear(cursor: SeriesTime): {
  current: typeof HSE_SAFETY_MONTHS;
  prior: typeof HSE_SAFETY_MONTHS;
  year: number;
} {
  const { year: cursorYear, month: cursorMonth } = dateParts(cursor);
  const year = cursorMonth >= 3 ? cursorYear : cursorYear - 1;
  const start = year * 12 + 2;
  const currentIndex = cursorMonthIndex(cursor);
  const available = safetyMonthsAvailableAt(cursor);
  return {
    current: available.filter((row) => monthIndex(row.month) >= start && monthIndex(row.month) <= currentIndex),
    prior: available.filter((row) => monthIndex(row.month) >= start - 12 && monthIndex(row.month) < start),
    year,
  };
}

function rollingTwelveMonths(cursor: SeriesTime) {
  const windowEnd = dateAtUtcMidnight(cursor) + DAY_MILLISECONDS;
  const windowStart = windowEnd - ROLLING_DAYS * DAY_MILLISECONDS;
  return safetyMonthsAvailableAt(cursor).flatMap((row) => {
    const { year, month } = dateParts(`${row.month}-01`);
    const monthStart = Date.UTC(year, month - 1, 1);
    const monthEnd = Date.UTC(year, month, 1);
    const observedEnd = row.asOf
      ? dateAtUtcMidnight(row.asOf) + DAY_MILLISECONDS
      : monthEnd;
    const overlapStart = Math.max(monthStart, windowStart);
    const overlapEnd = Math.min(monthEnd, observedEnd, windowEnd);
    const observedDays = (observedEnd - monthStart) / DAY_MILLISECONDS;
    const overlapDays = Math.max(0, (overlapEnd - overlapStart) / DAY_MILLISECONDS);
    if (overlapDays === 0 || observedDays <= 0) return [];
    const weight = overlapDays / observedDays;
    return [{
      ...row,
      overlapDays,
      observedDays,
      weight,
      recordableInjuries: row.recordableInjuries * weight,
      lostTimeInjuries: row.lostTimeInjuries * weight,
      restrictedWorkCases: row.restrictedWorkCases * weight,
      nearMisses: row.nearMisses * weight,
      hazardReports: row.hazardReports * weight,
      hoursWorked: row.hoursWorked * weight,
      toolboxTalksCompleted: row.toolboxTalksCompleted * weight,
      toolboxTalksScheduled: row.toolboxTalksScheduled * weight,
    }];
  });
}

function dueSoonWindowMinutes(obligation: (typeof HSE_OBLIGATIONS)[number]): number {
  const periodMinutes = diffMinutes(obligation.nextDueAt, obligation.lastCompletedAt);
  return Math.min(MAX_DUE_SOON_MINUTES, Math.max(0, periodMinutes * 0.25));
}

function seatLabel(seatId: string): string {
  return BUNKER_HILL_SEATS.find((seat) => seat.id === seatId)?.label ?? seatId;
}

function personName(personId: string): string {
  return BUNKER_HILL_PERSONS.find((person) => person.id === personId)?.name ?? personId;
}

function livePermitStatus(permit: (typeof HSE_PERMITS)[number], cursor: SeriesTime): HsePermitRow['status'] {
  if (permit.closedAt && diffMinutes(permit.closedAt, cursor) <= 0) return 'Closed';
  if (diffMinutes(permit.issuedAt, cursor) > 0) return 'Scheduled';
  if (diffMinutes(permit.expiresAt, cursor) <= 0) return 'Expired';
  return 'Active';
}

export function hsePermitRegisterView(state: SessionState): HsePermitRegisterView {
  const cursor = state.cursor;
  const rows = HSE_PERMITS.map((permit): HsePermitRow => ({
    id: permit.id,
    type: permit.type,
    equipmentId: permit.equipmentId,
    location: permit.location,
    crewPersonIds: permit.crewPersonIds,
    crew: permit.crewPersonIds.map(personName).join(' + '),
    issued: formatDateTime(permit.issuedAt),
    expires: formatDateTime(permit.expiresAt),
    area: permit.area,
    status: livePermitStatus(permit, cursor),
  }));
  const active = HSE_PERMITS.filter((permit) => livePermitStatus(permit, cursor) === 'Active');
  const closedToday = HSE_PERMITS.filter(
    (permit) => permit.closedAt?.slice(0, 10) === cursor.slice(0, 10) && diffMinutes(permit.closedAt, cursor) <= 0,
  );
  const expiring = active.filter((permit) => {
    const remaining = diffMinutes(permit.expiresAt, cursor);
    return remaining >= 0 && remaining <= TWO_HOURS_MINUTES;
  });
  return {
    activeCount: active.length,
    activeCaption: [...new Set(active.map((permit) => permit.type.toLocaleLowerCase('en-US')))].join(' · '),
    closedTodayCount: closedToday.length,
    closedTodayCaption: closedToday.map((permit) => permit.id).join(' · '),
    expiringWithinTwoHoursCount: expiring.length,
    expiringCaption: expiring
      .map((permit) => `${permit.id} ${permit.type.toLocaleLowerCase('en-US')} expires ${permit.expiresAt.slice(11, 16)}`)
      .join(' · '),
    rows,
  };
}

function formattedReading(readingId: string, value: number): string {
  const reading = readingByIdLookup(readingId);
  if (!reading) return String(value);
  const decimals = reading.decimals ?? 0;
  const number = value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${number} ${reading.unit}`;
}

export function regulatoryComplianceRows(state: SessionState): RegulatoryObligationRow[] {
  return HSE_OBLIGATIONS.map((obligation) => {
    const dueInMinutes = diffMinutes(obligation.nextDueAt, state.cursor);
    const readingValue = obligation.readingId ? valueAt(obligation.readingId, state) : undefined;
    return {
      id: obligation.id,
      requirement: obligation.requirement,
      frequency: obligation.frequency,
      lastCompleted: formatDate(obligation.lastCompletedAt),
      nextDue: formatDate(obligation.nextDueAt),
      status: dueInMinutes <= 0 ? 'Overdue' : dueInMinutes <= dueSoonWindowMinutes(obligation) ? 'Due soon' : 'Current',
      authority: obligation.authority,
      ownerSeatId: obligation.ownerSeatId,
      ...(obligation.readingId ? { readingId: obligation.readingId } : {}),
      ...(readingValue !== undefined
        ? { readingValue, readingLabel: formattedReading(obligation.readingId!, readingValue) }
        : {}),
    };
  });
}

function inspectionSummary(cursor: SeriesTime): { completed: number; total: number; open: number; nextDueAt?: SeriesTime } {
  const month = cursor.slice(0, 7);
  const inspections = HSE_INSPECTIONS.filter((inspection) => inspection.dueAt.slice(0, 7) === month);
  const incomplete = inspections
    .filter((inspection) => !inspection.completedAt || diffMinutes(inspection.completedAt, cursor) > 0)
    .sort((a, b) => diffMinutes(a.dueAt, b.dueAt));
  return {
    total: inspections.length,
    completed: inspections.filter(
      (inspection) => inspection.completedAt && diffMinutes(inspection.completedAt, cursor) <= 0,
    ).length,
    open: incomplete.length,
    ...(incomplete[0] ? { nextDueAt: incomplete[0].dueAt } : {}),
  };
}

function upcomingRequirements(cursor: SeriesTime): HseRequirementRow[] {
  return HSE_REQUIREMENTS
    .filter((requirement) => diffMinutes(requirement.dueAt, cursor) >= 0)
    .sort((a, b) => diffMinutes(a.dueAt, b.dueAt))
    .map((requirement) => {
      const dueInMinutes = diffMinutes(requirement.dueAt, cursor);
      return {
        id: requirement.id,
        label: requirement.label,
        dueAt: requirement.dueAt,
        dueLabel: formatDateTime(requirement.dueAt),
        detail: `${requirement.authority} · ${seatLabel(requirement.ownerSeatId)}`,
        tone: dueInMinutes <= 24 * 60 ? 'warn' : 'info',
      };
    });
}

export function hseDashboardView(state: SessionState): HseDashboardView {
  const cursor = state.cursor;
  const periods = monthsForOperatingYear(cursor);
  const currentYear = sumMonths(periods.current);
  const priorYear = sumMonths(periods.prior);
  const rolling = sumMonths(rollingTwelveMonths(cursor));
  const permits = hsePermitRegisterView(state);
  const obligations = regulatoryComplianceRows(state);
  const inspections = inspectionSummary(cursor);
  const dueSoon = obligations.filter((row) => row.status === 'Due soon');
  const nextDue = dueSoon[0];
  const startLabel = `Mar ${periods.year}`;
  const currentLabel = `${startLabel}–${formatDate(cursor)}`;
  const priorLabel = `Mar ${periods.year - 1}–Feb ${periods.year}`;
  const ltiFreeDays = daysBetween(LAST_LTI_DATE, cursor);
  const metrics = safetyMetricRows(currentYear, priorYear);
  const upcoming = upcomingRequirements(cursor);

  return {
    kpis: [
      {
        id: 'lti-free-days',
        label: 'LTI-free days',
        value: `${ltiFreeDays} days`,
        sub: `Since ${formatDate(LAST_LTI_DATE)}`,
        tone: 'ok',
      },
      {
        id: 'active-permits',
        label: 'Active permits today',
        value: String(permits.activeCount),
        sub: permits.activeCaption,
        tone: 'info',
      },
      {
        id: 'regulatory-due-soon',
        label: 'Regulatory items due soon',
        value: String(dueSoon.length),
        sub: nextDue ? `${nextDue.requirement} · ${nextDue.nextDue}` : '',
        tone: dueSoon.length > 0 ? 'warn' : 'ok',
      },
      {
        id: 'hse-inspections',
        label: 'HSE inspections this month',
        value: `${inspections.completed}/${inspections.total}`,
        sub: inspections.nextDueAt
          ? `${inspections.open} open · next due ${formatShortDate(inspections.nextDueAt)}`
          : `${inspections.open} open`,
        tone: inspections.completed === inspections.total ? 'ok' : 'warn',
      },
    ],
    safety: {
      trifr: rolling.hoursWorked > 0 ? roundOne((rolling.recordableInjuries * HSE_RATE_BASIS_HOURS) / rolling.hoursWorked) : 0,
      ltifr: rolling.hoursWorked > 0 ? roundOne((rolling.lostTimeInjuries * HSE_RATE_BASIS_HOURS) / rolling.hoursWorked) : 0,
      priorOperatingYearTrifr: priorYear.hoursWorked > 0
        ? roundOne((priorYear.recordableInjuries * HSE_RATE_BASIS_HOURS) / priorYear.hoursWorked)
        : 0,
      rateBasisHours: HSE_RATE_BASIS_HOURS,
      periodMeta: `Year 1 from 1 Mar ${periods.year} · prior year Mar ${periods.year - 1} – Feb ${periods.year}`,
      yearToDateLabel: `Year 1 · ${currentLabel}`,
      priorOperatingYearLabel: `Prior operating year · ${priorLabel}`,
      metrics,
    },
    upcoming,
  };
}

// --- The HSE page (locked comp `compliance.html`, variant B) ----------------
// Header fact, one four-cell band, the 8/4 row (safety performance · Due next)
// and the four drill-down tabs. Every figure below derives from the HSE, org,
// reading and crew seeds at the session cursor; the components only lay out.

/** A run of comparison text; `strong` is the lead term, `tone` marks the one
 *  exception a cell carries. */
export type HseTextRun = { text: string; strong?: boolean; tone?: 'warn' };

export type HseHeaderFact = {
  label: string;
  days: number;
  unit: string;
  comparison: readonly HseTextRun[];
};

export type HseBandCellId = 'trifr' | 'permits' | 'regulatory' | 'inspections';

export type HseBandCell = {
  id: HseBandCellId;
  name: string;
  figure: string;
  /** Denominator set small on the figure's baseline (`of 8`). */
  of?: string;
  comparison: readonly HseTextRun[];
  /** Only an exception carries a state rule (adjudication 4). */
  tone: 'warn' | null;
};

export type HseRecordableMonth = {
  id: string;
  label: string;
  kind: 'none' | 'recordable' | 'lti';
  /** The operating year starts at this month (a dashed rule before it). */
  yearStart: boolean;
};

export type HseDueNextRow = {
  id: string;
  at: SeriesTime;
  /** `12:45` today, `15 Jun` later. */
  when: string;
  what: string;
  detail?: string;
  owner: string;
  warn: boolean;
};

export type HseDueNext = {
  today: readonly HseDueNextRow[];
  todayLabel: string;
  later: readonly HseDueNextRow[];
  laterLabel: string;
};

export type PermitDayStatus =
  | { kind: 'under-2h'; label: string }
  | { kind: 'active'; label: string }
  | { kind: 'closed'; label: string }
  | { kind: 'expired'; label: string }
  | { kind: 'scheduled'; label: string };

export type PermitDayRow = {
  id: string;
  type: string;
  work: string;
  area: string;
  issued: string;
  crew: readonly string[];
  /** Percent positions on the shift axis. `worked` is issue → cursor (or
   *  issue → close for a closed permit); `ahead` is cursor → expiry. */
  bar: {
    worked: { left: number; width: number };
    ahead?: { left: number; width: number };
    closed: boolean;
    warn: boolean;
  };
  expires: string;
  left?: string;
  warn: boolean;
  status: PermitDayStatus;
};

export type PermitDayRegister = {
  ticks: readonly { label: string; left: number }[];
  cursorLeft: number;
  cursorClock: string;
  rows: readonly PermitDayRow[];
};

export type RegulatoryRegisterRow = RegulatoryObligationRow & {
  dueInDays: number;
  /** Share of the completion cycle elapsed at the cursor, 0–100. */
  cycleElapsedPct: number;
  /** Where the due-soon window starts on the cycle bar, 0–100. */
  dueSoonStartPct: number;
  owner: string;
  reading?: { figure: string; rest: string };
};

export type RegulatoryRegister = {
  rows: readonly RegulatoryRegisterRow[];
  /** `25 % of cycle, max 14 d` — the transform's own due-soon rule. */
  dueSoonRule: string;
};

export type SafetyRecordMonth = {
  id: string;
  label: string;
  /** Set under the first month of each calendar and operating year. */
  year?: string;
  nearMisses: number;
  hazardReports: number;
  partial: boolean;
};

export type SafetyRecord = {
  months: readonly SafetyRecordMonth[];
  max: number;
  rangeLabel: string;
  window: { left: number; label: string };
  yearOne: { left: number; label: string };
  partialNote: string;
};

export type RollingFact = { id: string; label: string; value: string; sub?: string };

export type CrewKpiCell = {
  id: string;
  label: string;
  value: string;
  sub: string;
  delta?: { arrow: '▲' | '▼'; text: string; tone: 'ok' | 'warn' | 'bad' };
};

export type CrewTrendBar = { id: string; day: string; value: number; height: number };

export type CrewTrend = {
  bars: readonly CrewTrendBar[];
  target: number;
  targetBottom: number;
  floor: number;
  meta: string;
};

export type CrewGap = { id: string; count: string; text: string; crew: string };

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;
const LATER_WINDOW_DAYS = 30;
const CREW_TREND_FLOOR = 70;
/** The monthly inspection round is the Superintendent's; the inspection seed
 *  carries no owner of its own, so the queue reads the seat that runs it. */
export const HSE_INSPECTION_SEAT_ID = 'seat-supt';

function clockOf(value: SeriesTime): string {
  return value.slice(11, 16);
}

function sameDate(a: string, b: string): boolean {
  return a.slice(0, 10) === b.slice(0, 10);
}

function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

/** The seat's holder at the cursor, else the seat's own label (a seat with
 *  no person stays the seat: `Maintenance`). */
export function seatOwnerName(seatId: string, cursor: SeriesTime): string {
  const assignment = BUNKER_HILL_ASSIGNMENTS.find(
    (row) => row.seatId === seatId && diffMinutes(`${row.from}T00:00`, cursor) <= 0,
  );
  if (assignment) return personName(assignment.personId);
  return seatLabel(seatId).replace(/\s*\(.*\)$/, '');
}

function durationLeft(minutes: number): string {
  const whole = Math.max(0, Math.round(minutes));
  const hours = Math.floor(whole / 60);
  const rest = whole % 60;
  return hours > 0 ? `${hours} h ${String(rest).padStart(2, '0')} m left` : `${rest} min left`;
}

function shortPermitId(id: string): string {
  return id.replace(/^PTW-\d{4}-/, 'PTW-');
}

function permitWork(location: string): string {
  return location.split(' — ')[0] ?? location;
}

export function hseHeaderFact(state: SessionState): HseHeaderFact {
  return {
    label: 'LTI-free',
    days: daysBetween(LAST_LTI_DATE, state.cursor),
    unit: 'days',
    comparison: [{ text: 'since the last LTI · ' }, { text: formatDate(LAST_LTI_DATE), strong: true }],
  };
}

/** The obligation's short name: its live reading's label where it has one
 *  (`TSF freeboard`), else the requirement itself. */
function obligationShortName(row: Pick<RegulatoryObligationRow, 'readingId' | 'requirement'>): string {
  const reading = row.readingId ? readingByIdLookup(row.readingId) : undefined;
  return reading?.label ?? row.requirement;
}

export function hseBand(state: SessionState): HseBandCell[] {
  const cursor = state.cursor;
  const dashboard = hseDashboardView(state);
  const permits = hsePermitRegisterView(state);
  const obligations = regulatoryComplianceRows(state);
  const inspections = inspectionSummary(cursor);
  const issuedToday = HSE_PERMITS.filter(
    (permit) => sameDate(permit.issuedAt, cursor) && diffMinutes(permit.issuedAt, cursor) <= 0,
  ).length;
  const dueSoon = obligations.filter((row) => row.status === 'Due soon').length;
  const next = [...HSE_OBLIGATIONS]
    .filter((row) => diffMinutes(row.nextDueAt, cursor) > 0)
    .sort((a, b) => diffMinutes(a.nextDueAt, b.nextDueAt))[0];
  const nextRow = next ? obligations.find((row) => row.id === next.id) : undefined;
  const expiring = permits.expiringWithinTwoHoursCount;
  const { month } = dateParts(cursor);

  return [
    {
      id: 'trifr',
      name: 'TRIFR · rolling 12 months',
      figure: dashboard.safety.trifr.toFixed(1),
      comparison: [
        { text: `LTIFR ${dashboard.safety.ltifr.toFixed(1)}`, strong: true },
        {
          text: ` · per ${dashboard.safety.rateBasisHours.toLocaleString('en-US')} h · prior year ${dashboard.safety.priorOperatingYearTrifr.toFixed(1)}`,
        },
      ],
      tone: null,
    },
    {
      id: 'permits',
      name: 'Active permits',
      figure: String(permits.activeCount),
      comparison: [
        { text: `of ${issuedToday} issued today · ${permits.closedTodayCount} closed` },
        ...(expiring > 0
          ? [
              { text: ' · ' },
              {
                text: `${expiring} ${plural(expiring, 'expires', 'expire')} within 2 h`,
                strong: true,
                tone: 'warn' as const,
              },
            ]
          : []),
      ],
      tone: expiring > 0 ? 'warn' : null,
    },
    {
      id: 'regulatory',
      name: 'Regulatory due soon',
      figure: String(dueSoon),
      comparison: [
        { text: `of ${obligations.length} obligations` },
        ...(next && nextRow
          ? [{ text: ' · next ' }, { text: `${obligationShortName(nextRow)} ${formatShortDate(next.nextDueAt)}`, strong: true }]
          : []),
      ],
      tone: null,
    },
    {
      id: 'inspections',
      name: `HSE inspections · ${MONTH_NAMES[month - 1]}`,
      figure: String(inspections.completed),
      of: `of ${inspections.total}`,
      comparison: [
        { text: `${inspections.open} open` },
        ...(inspections.nextDueAt
          ? [{ text: ' · next due ' }, { text: formatShortDate(inspections.nextDueAt), strong: true }]
          : []),
      ],
      tone: null,
    },
  ];
}

/** One mark per month for the recordables strip under the safety table: none,
 *  a recordable (MTI or RWC) or a lost-time injury. */
export function recordableMonths(state: SessionState): HseRecordableMonth[] {
  const { year: operatingYear } = monthsForOperatingYear(state.cursor);
  return safetyMonthsAvailableAt(state.cursor).map((row) => {
    const { month } = dateParts(`${row.month}-01`);
    return {
      id: row.month,
      label: row.asOf ? `1–${dateParts(row.asOf).day} ${MONTH_LABELS[month - 1]}` : MONTH_LABELS[month - 1]!,
      kind: row.lostTimeInjuries > 0 ? 'lti' : row.recordableInjuries > 0 ? 'recordable' : 'none',
      yearStart: row.month === `${operatingYear}-03`,
    };
  });
}

export function recordableStripLabel(state: SessionState): string {
  const months = safetyMonthsAvailableAt(state.cursor);
  const first = months[0];
  if (!first) return '';
  const { year, month } = dateParts(`${first.month}-01`);
  return `${MONTH_LABELS[month - 1]} ${year} – ${formatDate(state.cursor)}`;
}

/** A dated due event before it is placed in the queue; `at` decides the bucket,
 *  so `when` and `detail` are set once the bucket is known. */
type HseDueEvent = {
  id: string;
  at: SeriesTime;
  what: string;
  detail?: string;
  owner: string;
  warn: boolean;
};

/** The Superintendent's queue: today's permit expiries and dated due events
 *  before the day shift ends, then the next 30 days. Every row is a reference
 *  to the register that owns its date — a permit, an obligation's next-due
 *  event, the inspection register's next open event, or a requirement no
 *  register owns (the rescue drill and the monthly inspection round). No date
 *  here is typed in a second seed. */
export function hseDueNext(state: SessionState): HseDueNext {
  const cursor = state.cursor;
  const shiftEnd = shiftOf(cursor).end;
  /** Whole days to the event, the register's own `dueInDays` basis, so a date
   *  exactly 30 days out is not "inside 30 days". */
  const daysTo = (at: SeriesTime) => Math.round(diffMinutes(at, cursor) / (24 * 60));
  const events: HseDueEvent[] = [];

  for (const permit of HSE_PERMITS) {
    if (livePermitStatus(permit, cursor) !== 'Active') continue;
    const remaining = diffMinutes(permit.expiresAt, cursor);
    const warn = remaining >= 0 && remaining <= TWO_HOURS_MINUTES;
    events.push({
      id: permit.id,
      at: permit.expiresAt,
      what: `${shortPermitId(permit.id)} ${permit.type.toLocaleLowerCase('en-US')} expires`,
      detail: `${permitWork(permit.location)}${warn ? ' · close or extend' : ''}`,
      owner: personName(permit.crewPersonIds[0] ?? ''),
      warn,
    });
  }

  // The regulatory register's next-due events: one row per obligation.
  for (const obligation of HSE_OBLIGATIONS) {
    events.push({
      id: obligation.id,
      at: obligation.nextDueAt,
      what: obligationShortName(obligation),
      detail: obligation.authority,
      owner: seatOwnerName(obligation.ownerSeatId, cursor),
      warn: false,
    });
  }

  // The inspection register's next open event.
  const nextInspection = HSE_INSPECTIONS
    .filter((inspection) => !inspection.completedAt || diffMinutes(inspection.completedAt, cursor) > 0)
    .sort((a, b) => diffMinutes(a.dueAt, b.dueAt))[0];
  if (nextInspection) {
    events.push({
      id: nextInspection.id,
      at: nextInspection.dueAt,
      what: 'HSE inspection',
      owner: seatOwnerName(HSE_INSPECTION_SEAT_ID, cursor),
      warn: false,
    });
  }

  // The requirements no register owns: the rescue drill and the monthly round.
  for (const requirement of HSE_REQUIREMENTS) {
    events.push({
      id: requirement.id,
      at: requirement.dueAt,
      what: requirement.label,
      detail: requirement.authority,
      owner: seatOwnerName(requirement.ownerSeatId, cursor),
      warn: false,
    });
  }

  const today: HseDueNextRow[] = [];
  const later: HseDueNextRow[] = [];
  for (const event of events) {
    if (diffMinutes(event.at, cursor) < 0) continue;
    if (diffMinutes(event.at, shiftEnd) <= 0) {
      today.push({ ...event, when: clockOf(event.at) });
    } else if (daysTo(event.at) < LATER_WINDOW_DAYS) {
      later.push({ ...event, when: formatShortDate(event.at), detail: undefined });
    }
  }

  const byTime = (a: HseDueNextRow, b: HseDueNextRow) => diffMinutes(a.at, b.at);
  return {
    today: today.sort(byTime),
    todayLabel: `Today · before ${clockOf(shiftEnd)}`,
    later: later.sort(byTime),
    laterLabel: `Next ${LATER_WINDOW_DAYS} days`,
  };
}

/** The permit register drawn against the day shift: worked time solid to the
 *  cursor, permitted time dashed to expiry, closed permits soft to their close. */
export function permitDayRegister(state: SessionState): PermitDayRegister {
  const cursor = state.cursor;
  const shift = shiftOf(cursor);
  const span = diffMinutes(shift.end, shift.start);
  const at = (value: SeriesTime) => Math.min(100, Math.max(0, (diffMinutes(value, shift.start) / span) * 100));
  const cursorLeft = at(cursor);
  const ticks = Array.from({ length: Math.floor(span / 180) + 1 }, (_, index) => {
    const tick = addMinutes(shift.start, index * 180);
    return { label: clockOf(tick), left: at(tick) };
  });

  const rows = HSE_PERMITS.map((permit): PermitDayRow & { sortKey: number; group: number } => {
    const status = livePermitStatus(permit, cursor);
    const remaining = diffMinutes(permit.expiresAt, cursor);
    const warn = status === 'Active' && remaining >= 0 && remaining <= TWO_HOURS_MINUTES;
    const issuedLeft = at(permit.issuedAt);
    const closed = status === 'Closed' && permit.closedAt !== undefined;
    const workedEnd = closed ? at(permit.closedAt!) : status === 'Expired' ? at(permit.expiresAt) : cursorLeft;
    const base = {
      id: permit.id,
      type: permit.type,
      work: permit.location,
      area: permit.area,
      issued: clockOf(permit.issuedAt),
      crew: permit.crewPersonIds.map(personName),
      expires: clockOf(permit.expiresAt),
      warn,
    };
    const bar = {
      worked: { left: issuedLeft, width: Math.max(0, workedEnd - issuedLeft) },
      ...(status === 'Active' ? { ahead: { left: cursorLeft, width: Math.max(0, at(permit.expiresAt) - cursorLeft) } } : {}),
      closed: status !== 'Active',
      warn,
    };
    if (status === 'Active') {
      return {
        ...base,
        bar,
        left: durationLeft(remaining),
        status: warn ? { kind: 'under-2h', label: 'Under 2 h' } : { kind: 'active', label: 'Active' },
        group: 0,
        sortKey: remaining,
      };
    }
    if (closed) {
      return {
        ...base,
        bar,
        status: { kind: 'closed', label: `Closed ${clockOf(permit.closedAt!)}` },
        group: 1,
        sortKey: -diffMinutes(permit.closedAt!, cursor),
      };
    }
    return {
      ...base,
      bar,
      status: status === 'Expired' ? { kind: 'expired', label: 'Expired' } : { kind: 'scheduled', label: 'Scheduled' },
      group: 2,
      sortKey: diffMinutes(permit.expiresAt, cursor),
    };
  });

  return {
    ticks,
    cursorLeft,
    cursorClock: clockOf(cursor),
    rows: rows
      .sort((a, b) => a.group - b.group || a.sortKey - b.sortKey)
      .map(({ sortKey: _sortKey, group: _group, ...row }) => row),
  };
}

function readingComparison(readingId: string, value: number): { figure: string; rest: string } | undefined {
  const reading = readingByIdLookup(readingId);
  if (!reading) return undefined;
  const decimals = reading.decimals ?? 0;
  const format = (amount: number) =>
    amount.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  const spec = reading.spec;
  const band = reading.band;
  if (spec?.lo !== undefined && spec.hi !== undefined && spec.lo === spec.hi) {
    return { figure: `${format(value)} of ${format(spec.hi)}`, rest: ` ${reading.unit} ${value >= spec.hi ? 'compliant' : 'short'}` };
  }
  if (spec?.lo !== undefined && spec.hi === undefined) {
    return { figure: `${format(value)} ${reading.unit}`, rest: ` · minimum ${format(spec.lo)} ${reading.unit}` };
  }
  if (band?.lo !== undefined && band.hi !== undefined && band.lo === -band.hi) {
    const net = / net$/.test(reading.label) ? ' net' : '';
    const inside = value >= band.lo && value <= band.hi;
    return { figure: `${format(value)} ${reading.unit}`, rest: `${net} · ${inside ? 'inside' : 'outside'} ±${format(band.hi)} band` };
  }
  return { figure: `${format(value)} ${reading.unit}`, rest: '' };
}

/** Obligations sorted by next due, each with a cycle bar that decodes the
 *  due-soon rule (25 % of the cycle, capped at 14 days), a named owner and
 *  its live reading beside its comparison. */
export function regulatoryRegister(state: SessionState): RegulatoryRegister {
  const cursor = state.cursor;
  const rows = regulatoryComplianceRows(state).map((row): RegulatoryRegisterRow => {
    const obligation = HSE_OBLIGATIONS.find((item) => item.id === row.id)!;
    const period = diffMinutes(obligation.nextDueAt, obligation.lastCompletedAt);
    const elapsed = diffMinutes(cursor, obligation.lastCompletedAt);
    const window = dueSoonWindowMinutes(obligation);
    const reading = row.readingId && row.readingValue !== undefined ? readingComparison(row.readingId, row.readingValue) : undefined;
    return {
      ...row,
      dueInDays: Math.round(diffMinutes(obligation.nextDueAt, cursor) / (24 * 60)),
      cycleElapsedPct: period > 0 ? Math.min(100, Math.max(0, (elapsed / period) * 100)) : 100,
      dueSoonStartPct: period > 0 ? Math.max(0, (1 - window / period) * 100) : 0,
      owner: seatOwnerName(obligation.ownerSeatId, cursor),
      ...(reading ? { reading } : {}),
    };
  });
  const dueAt = (row: RegulatoryRegisterRow) => HSE_OBLIGATIONS.find((item) => item.id === row.id)!.nextDueAt;
  return {
    rows: rows.sort((a, b) => diffMinutes(dueAt(a), dueAt(b))),
    dueSoonRule: `25 % of cycle, max ${MAX_DUE_SOON_MINUTES / (24 * 60)} d`,
  };
}

/** Month-by-month near misses and hazard reports, with the rolling-12 window
 *  and the operating-year boundary placed on the same month axis. */
export function safetyRecord(state: SessionState): SafetyRecord {
  const cursor = state.cursor;
  const months = safetyMonthsAvailableAt(cursor);
  const { year: operatingYear } = monthsForOperatingYear(cursor);
  const count = months.length;
  const firstIndex = months[0] ? monthIndex(months[0].month) : 0;
  const windowEnd = dateAtUtcMidnight(cursor) + DAY_MILLISECONDS;
  const windowStart = new Date(windowEnd - ROLLING_DAYS * DAY_MILLISECONDS);
  const startYear = windowStart.getUTCFullYear();
  const startMonth = windowStart.getUTCMonth() + 1;
  const startDay = windowStart.getUTCDate();
  const daysInStartMonth = new Date(Date.UTC(startYear, startMonth, 0)).getUTCDate();
  const startOffset = startYear * 12 + (startMonth - 1) - firstIndex + (startDay - 1) / daysInStartMonth;
  const yearOneOffset = operatingYear * 12 + 2 - firstIndex;
  const partial = months.find((row) => row.asOf);
  const rows = months.map((row): SafetyRecordMonth => {
    const { year, month } = dateParts(`${row.month}-01`);
    const firstOfYear = month === 1 || month === 3 || row === months[0];
    return {
      id: row.month,
      label: row.asOf ? `1–${dateParts(row.asOf).day} ${MONTH_LABELS[month - 1]}` : MONTH_LABELS[month - 1]!,
      ...(firstOfYear && !row.asOf ? { year: String(year) } : {}),
      nearMisses: row.nearMisses,
      hazardReports: row.hazardReports,
      partial: Boolean(row.asOf),
    };
  });
  return {
    months: rows,
    max: Math.max(1, ...months.flatMap((row) => [row.nearMisses, row.hazardReports])),
    rangeLabel: recordableStripLabel(state),
    window: {
      left: count > 0 ? (startOffset / count) * 100 : 0,
      label: `Rolling 12 months · ${formatDate(windowStart.toISOString().slice(0, 10))} – ${formatDate(cursor)}`,
    },
    yearOne: { left: count > 0 ? (yearOneOffset / count) * 100 : 0, label: 'Year 1' },
    partialNote: partial ? `${MONTH_NAMES[dateParts(`${partial.month}-01`).month - 1]} to the ${clockOf(cursor)} cursor, faded` : '',
  };
}

/** The rolling-12 derivation behind the band's TRIFR, as facts. */
export function rollingTwelveFacts(state: SessionState): RollingFact[] {
  const cursor = state.cursor;
  const rows = rollingTwelveMonths(cursor);
  const total = sumMonths(rows);
  const windowEnd = dateAtUtcMidnight(cursor) + DAY_MILLISECONDS;
  const windowStart = new Date(windowEnd - ROLLING_DAYS * DAY_MILLISECONDS).toISOString().slice(0, 10);
  const lti = Math.round(total.lostTimeInjuries);
  const rwc = Math.round(total.restrictedWorkCases);
  const recordable = Math.round(total.recordableInjuries);
  const mti = recordable - lti - rwc;
  const prorated = rows.find((row) => row.weight < 1 && !row.asOf);
  const proratedLabel = prorated
    ? (() => {
        const { year, month } = dateParts(`${prorated.month}-01`);
        return `${MONTH_LABELS[month - 1]} ${year} prorated ${Math.round(prorated.overlapDays)} of ${Math.round(prorated.observedDays)} days`;
      })()
    : undefined;
  return [
    { id: 'window', label: 'Window', value: `${formatDate(windowStart)} – ${formatDate(cursor)}` },
    { id: 'recordables', label: 'Recordable injuries', value: String(recordable), sub: `${mti} MTI · ${rwc} RWC · ${lti} LTI` },
    { id: 'hours', label: 'Worker-hours', value: Math.round(total.hoursWorked).toLocaleString('en-US'), ...(proratedLabel ? { sub: proratedLabel } : {}) },
    { id: 'basis', label: 'Rate basis', value: `${HSE_RATE_BASIS_HOURS.toLocaleString('en-US')} h`, sub: 'MSHA / OSHA' },
    { id: 'operating-year', label: 'Operating year', value: `${MONTH_NAMES[2]} to ${MONTH_NAMES[1]}` },
  ];
}

/** The crew tab's four facts: the deck tiles with their trailing delta split
 *  off, and `vs target` added only where the line has not named the target. */
export function crewKpiCells(): CrewKpiCell[] {
  return kpisForComplianceTab('crew').map((tile) => {
    const match = tile.sub.match(/ · ([+−▲▼])\s*(.+)$/);
    if (!match) return { id: tile.id, label: tile.label, value: tile.value, sub: tile.sub };
    const sub = tile.sub.slice(0, -match[0].length);
    const down = match[1] === '−' || match[1] === '▼';
    const text = /target/.test(sub) ? match[2]! : `${match[2]} vs target`;
    return {
      id: tile.id,
      label: tile.label,
      value: tile.value,
      sub,
      delta: { arrow: down ? '▼' : '▲', text, tone: tile.tone },
    };
  });
}

/** The last seven complete production days (D25) before the cursor, labelled
 *  by date and read on an axis from 70 % against the 95 % target. Jun is
 *  carried by the caption rather than each tick. The last bar is the day the
 *  plant-compliance figure reads. */
export function crewTrend(): CrewTrend {
  const bundle = CREW_COMPLIANCE_TREND_7D;
  const points = bundle.series[0]?.points ?? [];
  const target = bundle.references?.[0]?.value ?? 95;
  const span = 100 - CREW_TREND_FLOOR;
  const height = (value: number) => Math.min(100, Math.max(0, ((value - CREW_TREND_FLOOR) / span) * 100));
  const first = bundle.categories[0] ?? '';
  const last = bundle.categories[bundle.categories.length - 1] ?? '';
  const { month } = dateParts(HSE_CURSOR);
  return {
    bars: bundle.categories.map((day, index) => ({
      id: `crew-day-${index + 1}`,
      day,
      value: points[index] ?? 0,
      height: height(points[index] ?? 0),
    })),
    target,
    targetBottom: height(target),
    floor: CREW_TREND_FLOOR,
    meta: `complete plant days ${first}–${last} ${MONTH_LABELS[month - 1]} · dashed ${target} % target · axis ${CREW_TREND_FLOOR}`,
  };
}

export function crewGaps(): CrewGap[] {
  const table = tablesForComplianceTab('crew').find((item) => item.caption.startsWith('Top compliance gaps'));
  return (table?.rows ?? []).map((row) => ({
    id: row.id,
    count: row.cells[0] ?? '',
    text: row.cells[1] ?? '',
    crew: row.cells[2] ?? '',
  }));
}

/** The crew matrix's caption: the seed keeps the title, and the window is
 *  derived from the same D25 complete-day trend the card draws — seven complete
 *  days 01–07 Jun 2026. It says "days", never "week": the plant week is 2–8 Jun
 *  and the running 08 Jun is not part of this window. */
export function crewMatrixCaption(caption: string): { title: string; meta?: string } {
  const [title, seededMeta] = caption.split(' · ');
  if (!seededMeta) return { title: caption };
  const bundle = CREW_COMPLIANCE_TREND_7D;
  const first = bundle.categories[0] ?? '';
  const last = bundle.categories[bundle.categories.length - 1] ?? '';
  const { month, year } = dateParts(HSE_CURSOR);
  return {
    title: title ?? caption,
    meta: `${bundle.categories.length} complete days · ${first}–${last} ${MONTH_LABELS[month - 1]} ${year}`,
  };
}

/** Each tab carries its headline before it is opened. */
export function complianceTabHeadlines(state: SessionState): Record<ComplianceTab, string> {
  const permits = hsePermitRegisterView(state);
  const obligations = regulatoryComplianceRows(state);
  const current = obligations.filter((row) => row.status === 'Current').length;
  const plant = kpisForComplianceTab('crew').find((tile) => tile.id === 'crew-plant');
  const target = CREW_COMPLIANCE_TREND_7D.references?.[0]?.value;
  return {
    permits: `${permits.activeCount} active · ${permits.expiringWithinTwoHoursCount} under 2 h`,
    compliance: `${current} of ${obligations.length} current`,
    crew: plant && target !== undefined ? `${plant.value} vs ${target}%` : '',
    trend: 'near misses · hazard reports',
  };
}
